import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScaleLoader } from 'react-spinners';
import Container from '@/ui/_lib/Container';
import Button from '@/ui/_lib/Button';
import template1 from '@/assets/image/templateone.png';
import template2 from '@/assets/image/templatetwo.png';
import template3 from '@/assets/image/templatethree.png';
import useAuth from '@/hooks/useAuth';
import { useUser } from '@/contexts/UserContext/UserContext';
import { UserService } from '@/ui/_apiUrls';

function ChooseTemplate() {
  const { useAuthenticatedPage } = useAuth();
  const { user, setUser, isLoading } = useUser();
  const navigate = useNavigate();
  // Use the user's saved template as default, or 0 (template 1) if not available
  const defaultTemplate = user?.template ? parseInt(user.template.toString(), 10) - 1 : 0;
  const [activeTemplate, setActiveTemplate] = useState(defaultTemplate);

  // Get the patch mutation from UserService
  const { isLoading: isPatchingUser } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        // Update user context with the response
        const updatedUser = {
          ...user,
          ...response.result,
        };
        setUser(updatedUser);
      },
      onError: () => {
        // Using console.error is acceptable for error logging in development
        // eslint-disable-next-line no-console
      },
    },
  });

  // Use authenticated page hook but allow incomplete profiles since we're checking profile completion separately
  useAuthenticatedPage({ allowIncompleteProfile: true });

  // Redirect to complete profile if profile is not complete
  useEffect(() => {
    if (user && user.profileComplete === false) {
      navigate('/complete-profile');
    }
  }, [user, navigate]);

  const handleNext = () => {
    // Navigate to add links page with the selected template as a query parameter
    // Don't save the template to the user's profile yet
    navigate(`/add-links?template=${activeTemplate + 1}`);
  };

  const handleTemplateChange = (index: number) => {
    setActiveTemplate(index);
  };

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ScaleLoader color="#039BE5" />
      </div>
    );
  }

  if (user.profileComplete === false) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ScaleLoader color="#039BE5" />
      </div>
    );
  }

  return (
    <Container className="flex h-full w-full flex-col p-4 pt-[calc(env(safe-area-inset-top)_/_2)] md:p-8">
      <div className="flex w-full max-w-md flex-col gap-6 md:max-w-4xl md:gap-8 flex-1">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-[#475467] md:text-xl">Step 1</h1>
          <h2 className="text-xl font-bold text-[#333333] md:text-3xl">Choose Template</h2>
        </div>

        {/* Mobile: Single template view with navigation arrows */}
        <div className="relative mx-auto flex w-full max-w-[300px] flex-1 flex-col items-center md:hidden">
          {/* Template Name - Positioned even closer to template */}
          <div className="mb-1 text-center">
            <h3 className="text-lg font-semibold text-[#333333]">Template {activeTemplate + 1}</h3>
          </div>
          
          <div className="relative flex flex-col items-center justify-center flex-1">
            <div className="relative flex items-center justify-center">
              {/* Left Arrow - Invisible when on first template */}
              {activeTemplate === 0 ? (
                <div className="z-10 mr-2 w-10 h-10 invisible" aria-hidden="true"></div>
              ) : (
                <button
                  onClick={() => handleTemplateChange(activeTemplate - 1)}
                  className="z-10 mr-2 rounded-full border border-gray-200 bg-white p-3 shadow-md hover:bg-gray-50"
                  aria-label="Previous template"
                  type="button"
                >
                  <svg className="h-4 w-4 text-[#039BE5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              <div className="relative w-[248.63px]">
                <img
                  src={template1}
                  alt="Template Preview 1"
                  className={`w-[248.63px] h-[511px] object-cover rounded-xl ${activeTemplate === 0 ? 'block' : 'hidden'}`}
                />
                <img
                  src={template2}
                  alt="Template Preview 2"
                  className={`w-[248.63px] h-[511px] object-cover rounded-xl ${activeTemplate === 1 ? 'block' : 'hidden'}`}
                />
                <img
                  src={template3}
                  alt="Template Preview 3"
                  className={`w-[248.63px] h-[511px] object-cover rounded-xl ${activeTemplate === 2 ? 'block' : 'hidden'}`}
                />
              </div>

              {/* Right Arrow - Invisible when on last template */}
              {activeTemplate === 2 ? (
                <div className="z-10 ml-2 w-10 h-10 invisible" aria-hidden="true"></div>
              ) : (
                <button
                  onClick={() => handleTemplateChange(activeTemplate + 1)}
                  className="z-10 ml-2 rounded-full border border-gray-200 bg-white p-3 shadow-md hover:bg-gray-50"
                  aria-label="Next template"
                  type="button"
                >
                  <svg className="h-4 w-4 text-[#039BE5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Dots Indicator - Positioned with appropriate spacing */}
            <div className="mt-6 flex justify-center space-x-2">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateChange(index)}
                  className={`h-2 w-2 rounded-full ${activeTemplate === index ? 'bg-[#039BE5]' : 'bg-gray-300'}`}
                  aria-label={`Template ${index + 1}`}
                  type="button"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: All templates in a row */}
        <div className="hidden md:block">
            <div className="grid grid-cols-3 gap-6">
              {[
                { src: template1, alt: 'Template 1' },
                { src: template2, alt: 'Template 2' },
                { src: template3, alt: 'Template 3' },
              ].map((template, index) => (
                <div key={index} className="flex flex-col items-center gap-4">
                  <h3 className="text-lg font-semibold text-[#333333]">Template {index + 1}</h3>
                  <div
                    className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 ${
                      activeTemplate === index
                        ? 'scale-105 border-[#039BE5] shadow-lg'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleTemplateChange(index)}
                  >
                    <img src={template.src} alt={template.alt} className="w-full rounded-lg" />
                    {activeTemplate === index && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-[#039BE5]/20">
                        <div className="rounded-full bg-[#039BE5] p-2">
                          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        <div className="w-full md:w-auto md:mx-auto md:max-w-[300px] mt-auto">
          <Button
            onClick={handleNext}
            className="w-full gap-2 rounded-md px-4 py-2 md:px-6 md:py-3 md:text-lg"
            variant="primary"
            type="button"
            disabled={isPatchingUser}
          >
            {isPatchingUser ? 'Saving...' : 'Choose Template'}
          </Button>
        </div>
      </div>
    </Container>
  );
}

export default ChooseTemplate;
